export type DataStoreFieldType =
  | "TEXT"
  | "LONG_TEXT"
  | "NUMBER"
  | "BOOLEAN"
  | "DATE"
  | "DATETIME"
  | "SINGLE_SELECT"
  | "UUID";

export const DATASTORE_FIELD_TYPES: DataStoreFieldType[] = [
  "TEXT",
  "LONG_TEXT",
  "NUMBER",
  "BOOLEAN",
  "DATE",
  "DATETIME",
  "SINGLE_SELECT",
  "UUID",
];

export interface DataStoreFieldSettings {
  options?: string[];
  maxLength?: number;
  scale?: number;
}

export interface DataStoreField {
  id: string;
  name: string;
  type: DataStoreFieldType;
  ordinal: number;
  isPrimary?: boolean;
  required?: boolean;
  uniqueKey?: boolean;
  dbIndex?: boolean;
  settings?: DataStoreFieldSettings;
}

/**
 * What an organization may do with a table it does not own. Mirrors the backend DataStoreAccess ladder:
 * each level contains the one before it, and none of them is ownership.
 */
export type DataStoreAccessLevel = "READ" | "READ_WRITE" | "MODIFY";

/** A grant of a table to one organization. */
export interface DataStoreShare {
  organizationId: string;
  name: string;
  access: DataStoreAccessLevel;
}

export interface DataStoreTable {
  id: string;
  name: string;
  description: string;
  organizationId: string;
  /** The owning organization's name. Not the viewer's: a granted table is owned elsewhere. */
  organizationName: string;
  rowCount: number;
  version: number;
  /** Whether the viewing organization owns this table, as opposed to holding a grant on it. */
  owned: boolean;
  /** The viewing organization's effective level; always MODIFY for the owner. */
  access: DataStoreAccessLevel;
  sharedWith: DataStoreShare[];
  modifiedAt: string;
  fields: DataStoreField[];
}

/** One page of organizations a table may be shared with. */
export interface ShareTargetPage {
  targets: { organizationId: string; name: string }[];
  total: number;
  page: number;
  size: number;
}

export type DataStoreCell = string | number | boolean | null;

export interface DataStoreRow {
  id: string;
  cells: Record<string, DataStoreCell>;
}

export interface RowPage {
  rows: DataStoreRow[];
  total: number;
  offset: number;
  nextCursor: string | null;
}

export interface RowQuery {
  page: number;
  pageSize: number;
  search?: string;
}

export type DataStoreJobState =
  | "PENDING"
  | "RUNNING"
  | "FINISHED"
  | "FAILED"
  | "CANCELLED";

export interface DataStoreJob {
  id: string;
  type: "IMPORT" | "EXPORT" | "CONVERT";
  tableId: string;
  state: DataStoreJobState;
  progressPercentage: number;
  processedRows: number;
  totalRows: number;
  report: string[];
  error?: string;
}

export interface ImportPreview {
  jobId: string;
  fileName: string;
  fileSize: number;
  header: string[];
  sampleRows: string[][];
  estimatedRows: number;
}

export interface ConvertPreview {
  total: number;
  convertible: number;
  failing: number;
  sampleErrors: string[];
}

export interface ColumnMapping {
  csvColumn: string;
  /** target field id, or one of the sentinels below */
  target: string;
  inferredType: DataStoreFieldType;
}

export const MAP_CREATE_NEW = "__create__";
export const MAP_SKIP = "__skip__";

/** Append every row, or merge onto the table's unique key (design §7.1). */
export type ImportMode = "append" | "upsert";

/** Keep the rows that validated, or abort the whole file on the first failure. */
export type ImportErrorMode = "partial" | "strict";

/**
 * The governor limits in force for this organization (design §10), as served by
 * GET /rest/v1/datastore/limits. They are runtime configuration, so the client asks
 * the server rather than carrying its own copy.
 */
export interface DataStoreLimits {
  maxTablesPerOrg: number;
  maxFieldsPerTable: number;
  maxRowsPerTable: number;
  maxRowsPerOrg: number;
  maxCellBytes: number;
  pageSizeLimit: number;
  batchRowsLimit: number;
  maxOffset: number;
  importMaxFileBytes: number;
}
